import { flow } from 'mobx';
import { Vessel } from '@modules/fleet/models';
import { alertStore } from '@modules/core/store/AlertStore';
import { apiStore } from '@modules/core/store/ApiStore';
import { Account } from '@modules/accounts/models';
import { HttpClient } from '@modules/core/utils';
import { FleetStore } from '@modules/fleet/store';

export class FleetStoreUsers extends FleetStore {
    constructor() {
        super();

        this.addVessels = this.addVessels.bind(this);
        this.loadVessels = this.loadVessels.bind(this);
        this.deleteVessels = this.deleteVessels.bind(this);
        this.loadOrganizationVessels = this.loadOrganizationVessels.bind(this);
    }

    loadOrganizationVessels = flow(function* (this: FleetStoreUsers) {
        if (!this.selectedOrganization?.id) {
            return [];
        }
        const { api, errorMessageHandler } = apiStore.api.vessels.getOrganizationVessels;
        try {
            return yield HttpClient.TCMS.GET(api(this.selectedOrganization.id));
        } catch (error) {
            alertStore.error(error, errorMessageHandler);
            return [];
        }
    });

    loadVessels = flow(function* (this: FleetStoreUsers, isUpdate = false) {
        this.vessels = [];

        if (!this.selectedAccount) {
            return;
        }

        const { api: getDirAssVesselsApi, errorMessageHandler: getDirAssVesselsErrorHandler } =
            apiStore.api.vessels.getOrganizationUserDirectlyAssignedVessels;
        let orgUser: Account = this.selectedAccount;

        if (isUpdate) {
            const { api: getUserApi, errorMessageHandler: getUserErrorHandler } =
                apiStore.api.users.getOrganizationUser;

            try {
                this.loadingStatusState.load();
                orgUser = yield HttpClient.TCMS.GET(
                    getUserApi(this.selectedAccount.id, this.selectedAccount.organization?.id)
                );
                this.updateSelectedAccount(orgUser);
                this.loadingStatusState.loadSuccess();
            } catch (error) {
                this.loadingStatusState.loadError();
                alertStore.error(error, getUserErrorHandler);
            }
        }

        try {
            this.loadingStatusState.load();
            const vesselsByOrganization: Vessel[] = yield this.loadOrganizationVessels();
            const directlyAssignedVessels: Vessel[] = yield HttpClient.TCMS.GET(
                getDirAssVesselsApi(this.selectedAccount.id, this.selectedAccount.organization?.id)
            );
            this.vessels = vesselsByOrganization
                .filter((vessel) => orgUser.vessels.includes(vessel.id))
                .map((vessel) => ({
                    ...vessel,
                    directlyAssigned: !!directlyAssignedVessels.find((directVessel) => directVessel.id === vessel.id)
                }));
            this.loadingStatusState.loadSuccess();
        } catch (error) {
            this.loadingStatusState.loadError();
            alertStore.error(error, getDirAssVesselsErrorHandler);
        }
    });

    addVessels = flow(function* (this: FleetStoreUsers, vesselsToAdd: Vessel[]) {
        if (!this.selectedAccount) {
            return;
        }
        const { api, successMessage, errorMessageHandler } =
            apiStore.api.vessels.putOrganizationUserDirectlyAssignedVessels;

        try {
            yield HttpClient.TCMS.PUT(
                api(this.selectedAccount.id, this.selectedAccount.organization?.id),
                vesselsToAdd.map((vessel) => vessel.id)
            );
            alertStore.success(successMessage);
        } catch (error) {
            alertStore.error(error, errorMessageHandler);
        }
    });

    deleteVessels = flow(function* (this: FleetStoreUsers, vesselsToDelete: Vessel[]) {
        if (!this.selectedAccount) {
            return;
        }
        const { api, successMessage, errorMessageHandler } =
            apiStore.api.vessels.putOrganizationUserDirectlyAssignedVessels;
        try {
            const newVessels = this.directlyAssignedVessels.filter((directlyAssigned) =>
                vesselsToDelete.find((vessel) => directlyAssigned.id !== vessel.id)
            );

            yield HttpClient.TCMS.PUT(
                api(this.selectedAccount.id, this.selectedAccount.organization?.id),
                newVessels.map((vessel) => vessel.id)
            );
            alertStore.success(successMessage);
        } catch (error) {
            alertStore.error(error, errorMessageHandler);
        }
    });
}
